import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Truck, Flag, X, Camera, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { EmptyState, Card } from "./fxui";

const TONE = { ready: "bg-[#D9933B]/15 text-[#B5771F]", assigned: "bg-[#2563EB]/15 text-[#2563EB]", out: "bg-[#7C3AED]/15 text-[#7C3AED]", attempted: "bg-[#B23B3B]/15 text-[#B23B3B]" };

export default function FxDelivery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [bulkZone, setBulkZone] = useState("");
  const [bulkRider, setBulkRider] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/fx/delivery/queue"); setItems(data.deliveries || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const zones = Array.from(new Set(items.map((d) => d.zone).filter(Boolean))).sort();
  const bulkAssign = async () => {
    if (!bulkZone || !bulkRider) return toast.error("Pick a zone and enter a rider");
    try { const { data } = await api.post("/fx/delivery/bulk-assign", { zone: bulkZone, rider: bulkRider });
      toast.success(`${data.assigned} order(s) assigned to ${bulkRider}`); setBulkRider(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not bulk-assign"); }
  };

  return (
    <div data-testid="fx-delivery">
      {zones.length > 0 && (
        <div className="bg-white border border-black/5 rounded-2xl p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="fx-delivery-bulk">
          <span className="text-sm font-semibold text-[#0D530E]">Assign a whole zone:</span>
          <select value={bulkZone} onChange={(e) => setBulkZone(e.target.value)} data-testid="fx-bulk-zone"
            className="bg-white border border-black/10 rounded-full px-4 py-2 text-sm">
            <option value="">Select zone…</option>
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          <input value={bulkRider} onChange={(e) => setBulkRider(e.target.value)} placeholder="Rider name" data-testid="fx-bulk-rider"
            className="flex-1 min-w-[160px] bg-white border border-black/10 rounded-full px-4 py-2 text-sm" />
          <button onClick={bulkAssign} data-testid="fx-bulk-assign"
            className="bg-[#306D29] hover:bg-[#0D530E] text-white px-4 py-2 rounded-full text-sm font-semibold">Assign zone</button>
        </div>
      )}
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>
        : items.length === 0 ? <EmptyState>No orders in the delivery queue. Released orders from inbound appear here.</EmptyState>
        : (
        <div className="space-y-2">
          {items.map((d) => (
            <button key={d.order_number} data-testid={`fx-delivery-row-${d.order_number}`} onClick={() => setActive(d.order_number)}
              className="w-full text-left bg-white border border-black/5 rounded-2xl px-5 py-4 hover:border-[#306D29]/40 flex items-center gap-3" style={{ transition: "border-color 0.15s ease" }}>
              <Truck size={18} className="text-[#306D29]" />
              <div className="flex-1">
                <p className="font-semibold text-[#0D530E] flex items-center gap-2">{d.order_number} {d.priority && <Flag size={13} className="text-[#D9933B] fill-[#D9933B]" />}</p>
                <p className="text-xs text-[#5C5C5C]">{d.customer_name} · {d.zone || d.address || "—"} {d.rider && `· ${d.rider}`}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${TONE[d.status] || ""}`}>{d.status}</span>
            </button>
          ))}
        </div>
      )}
      {active && <DeliveryModal item={items.find((x) => x.order_number === active)} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function DeliveryModal({ item, onClose }) {
  const onum = item.order_number;
  const [rider, setRider] = useState(item.rider || "");
  const [zone, setZone] = useState(item.zone || "");
  const [proof, setProof] = useState("");
  const [uploading, setUploading] = useState(false);
  const [attemptReason, setAttemptReason] = useState("");

  const assign = async () => { await api.post(`/fx/delivery/${onum}/assign`, { rider, zone }); toast.success("Rider assigned"); onClose(); };
  const out = async () => { await api.post(`/fx/delivery/${onum}/out`); toast.success("Out for delivery"); onClose(); };
  const delivered = async () => { await api.post(`/fx/delivery/${onum}/delivered`, { proof_url: proof }); toast.success("Delivered!"); onClose(); };
  const attempt = async () => { if (!attemptReason) return toast.error("Add a reason"); await api.post(`/fx/delivery/${onum}/attempt`, { reason: attemptReason }); toast.success("Attempt logged"); onClose(); };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProof(data.url); toast.success("Photo attached"); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FBF5DD] rounded-3xl p-7 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="fx-delivery-modal">
        <div className="flex justify-between items-start mb-4">
          <div><h3 className="font-display text-2xl text-[#0D530E]">{onum}</h3><p className="text-sm text-[#5C5C5C]">{item.customer_name} · {item.phone}</p></div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-4">{item.address}</p>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <input value={rider} onChange={(e) => setRider(e.target.value)} placeholder="Rider" data-testid="fx-delivery-rider" className="bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm" />
          <input value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Zone" data-testid="fx-delivery-zone" className="bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm" />
        </div>
        <button onClick={assign} data-testid="fx-delivery-assign" className="w-full py-2.5 rounded-full border border-black/10 text-sm font-semibold mb-2">Assign rider</button>
        <button onClick={out} data-testid="fx-delivery-out" className="w-full py-2.5 rounded-full bg-[#7C3AED] text-white text-sm font-semibold mb-4 flex items-center justify-center gap-2"><Truck size={16} /> Mark out for delivery</button>

        <Card className="mb-3">
          <label className="flex items-center gap-2 text-sm text-[#306D29] cursor-pointer mb-3">
            <Camera size={16} /> {uploading ? "Uploading…" : proof ? "Proof attached ✓" : "Attach proof of delivery"}
            <input type="file" accept="image/*" className="hidden" onChange={upload} />
          </label>
          <button onClick={delivered} data-testid="fx-delivery-delivered" className="w-full py-3 rounded-full bg-[#306D29] text-white text-sm font-semibold flex items-center justify-center gap-2"><CheckCircle2 size={16} /> Mark delivered</button>
        </Card>

        <div className="flex gap-2">
          <input value={attemptReason} onChange={(e) => setAttemptReason(e.target.value)} placeholder="Failed attempt reason" data-testid="fx-delivery-attempt-reason" className="flex-1 bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm" />
          <button onClick={attempt} data-testid="fx-delivery-attempt" className="px-4 rounded-xl bg-[#B23B3B] text-white text-sm font-semibold">Log attempt</button>
        </div>
      </div>
    </div>
  );
}
